import classNames from 'classnames';
import DisplayError from 'components/error/DisplayError';
import Loading from 'components/loading/Loading';
import {
  ExportMenu,
  PlatformForExportImage,
} from 'components/visualize/components';
import { isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import React, { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import {
  ComposedChart,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Bar,
  Line,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { UiIcon, UiTooltip } from 'ui-components';
import {
  StyledBarLineChart,
  StyledCustomLegend,
  StyledCustomTooltip,
  StyledLabelItem,
} from './BarLineChart.styles';

const LabelItem = ({ data }) => (
  <StyledLabelItem>
    <div className='flex align-center gap-6'>
      <span className='label' style={{ background: data.color }} />
      <span className='title'>{data.title}</span>
    </div>
    {data.value != null && data.value !== '' && (
      <span className='value'>
        {data.value}
        {data.suffix && (
          <span className='pl-2'>{data.suffix && data.suffix}</span>
        )}
      </span>
    )}
  </StyledLabelItem>
);

LabelItem.propTypes = {
  data: PropTypes.object.isRequired,
};

const CustomLegend = ({ legends }) => (
  <StyledCustomLegend>
    {legends &&
      Object.keys(legends)?.map((legend) => (
        <LabelItem data={legends[legend]} />
      ))}
  </StyledCustomLegend>
);

CustomLegend.propTypes = {
  legends: PropTypes.string.isRequired,
};

const CustomTooltip = ({ payload, data, order }) => {
  const row = payload?.[0]?.payload;
  const title = row?.name;
  /**
   * A series can plot one field and read another in the tooltip, which is what
   * lets a percentage bar report the raw count it was calculated from. The
   * tooltip can also list the series in its own order and under its own labels.
   */
  const items = (order ?? Object.keys(data)).map((key) => {
    const { tooltipKey, tooltipTitle, title: legendTitle } = data[key];
    return {
      title: tooltipTitle || legendTitle,
      color: data[key].color,
      value: tooltipKey
        ? row?.[tooltipKey]
        : payload.find((item) => item.dataKey === key)?.value,
      suffix: data[key].suffix || null,
    };
  });
  return (
    <StyledCustomTooltip>
      <p className='title'>{title}</p>
      {items.map((item) => (
        <LabelItem key={item.title} data={item} />
      ))}
    </StyledCustomTooltip>
  );
};

CustomTooltip.propTypes = {
  payload: PropTypes.array.isRequired,
  data: PropTypes.object.isRequired,
  order: PropTypes.array,
};

/**
 * Recharts computes "nice" tick steps with plain float division (e.g. 100/6),
 * which lands the last tick on things like 100.00999999999999. Rounding fixes
 * that for the fixed-domain NSS axes; charts that let Recharts pick the domain
 * (and may want fractional ticks) keep the default formatter.
 */
const formatTick = (value) => Math.round(value);

const BarLineChart = ({
  className = '',
  title = '',
  tooltip = '',
  noHeader = false,
  loading = false,
  error = {},
  refresh,
  actions,
  noActions = false,
  data,
  chartKeys,
  legends,
  customFilter,
  axisLabels,
  yAxisDomains,
  yAxisTicks,
  tooltipOrder,
  xAxisKey = 'name',
  hasLineAnimation = false,
}) => {
  const [fullScreen, setFullScreen] = useState(false);
  const chartRef = useRef(null);
  const wrapperRef = useRef(null);
  const { t } = useTranslation();
  const { platform: platformParam } = useParams();

  /**
   * Repeated labels (hourly buckets share the same clock time across days) make
   * a category axis resolve the hovered slot to the first match, so the active
   * dot and tooltip jump around. Charts pass a unique `xAxisKey` for the axis
   * and the `name` column is only used as the visible label.
   */
  const formatXTick = useMemo(() => {
    if (xAxisKey === 'name') return undefined;
    const labels = new Map(
      (data ?? []).map((row) => [row[xAxisKey], row.name])
    );
    return (value) => labels.get(value) ?? value;
  }, [data, xAxisKey]);

  const chart = useMemo(() => {
    return (
      <ResponsiveContainer width='100%' height='100%' ref={wrapperRef}>
        <ComposedChart data={data}>
          <CartesianGrid
            stroke='#ccc'
            yAxisId='left'
            horizontal={true}
            vertical={false}
          />
          <XAxis
            tickMargin={12}
            fontSize={12}
            fontWeight={500}
            stroke='#999'
            axisLine={false}
            tickSize={0}
            dataKey={xAxisKey}
            tickFormatter={formatXTick}
          />
          <YAxis
            tickMargin={38}
            fontSize={12}
            fontWeight={500}
            stroke='#999'
            axisLine={false}
            tickSize={0}
            yAxisId='left'
            orientation='left'
            width='auto'
            domain={yAxisDomains?.left}
            ticks={yAxisTicks?.left}
            tickFormatter={yAxisDomains ? formatTick : undefined}
            label={{
              fontSize: 10,
              value: axisLabels?.yAxis?.left,
              angle: 270,
              position: 'insideRight',
              offset: 78,
              style: { textAnchor: 'middle' },
              fill: '#9CAEBB',
            }}
          />
          <YAxis
            tickMargin={38}
            fontSize={12}
            fontWeight={500}
            stroke='#999'
            axisLine={false}
            tickSize={0}
            yAxisId='right'
            orientation='right'
            width='auto'
            domain={yAxisDomains?.right}
            ticks={yAxisTicks?.right}
            tickFormatter={yAxisDomains ? formatTick : undefined}
            label={{
              fontSize: 10,
              value: axisLabels?.yAxis?.right,
              angle: 270,
              position: 'insideRight',
              offset: 5,
              style: { textAnchor: 'middle' },
              fill: '#9CAEBB',
            }}
          />
          <Legend
            verticalAlign='bottom'
            content={<CustomLegend legends={legends} />}
          />
          {chartKeys?.barChartKeys.map(({ key, fill, stackId }) => {
            return (
              <Bar
                key={key}
                yAxisId='left'
                dataKey={key}
                fill={fill}
                stackId={stackId}
                barSize={24}
                radius={[3, 3, 0, 0]}
                stroke='#fff'
                strokeWidth={1}
              />
            );
          })}
          {chartKeys?.lineChartKeys.map(({ key, stroke }) => {
            return (
              <Line
                key={key}
                yAxisId='right'
                type='linear'
                dataKey={key}
                stroke={stroke}
                strokeWidth={2}
                isAnimationActive={hasLineAnimation}
                activeDot={{ isAnimationActive: hasLineAnimation }}
              />
            );
          })}

          <Tooltip
            cursor={false}
            content={<CustomTooltip data={legends} order={tooltipOrder} />}
          />
        </ComposedChart>
      </ResponsiveContainer>
    );
  }, [
    data,
    chartKeys,
    legends,
    axisLabels,
    yAxisDomains,
    yAxisTicks,
    tooltipOrder,
    xAxisKey,
    formatXTick,
    hasLineAnimation,
  ]);

  return (
    <StyledBarLineChart
      ref={chartRef}
      className={classNames(
        className,
        'bar-line-chart',
        fullScreen ? 'full-screen' : ''
      )}
    >
      {!noHeader && (
        <div component='CardHeader' className='card-header flex'>
          <div className='flex'>
            <div className='flex gap-4'>
              <h3 className='title'>{title}</h3>
              {tooltip && (
                <UiTooltip
                  className='excluded-from-image-download'
                  title={tooltip}
                >
                  <UiIcon icon='ri-information-line' color='#9CAEBB' />
                </UiTooltip>
              )}
            </div>
            <PlatformForExportImage platform={platformParam} />
          </div>
          {!noActions && (
            <div className='actions flex excluded-from-image-download'>
              {actions}
              <ExportMenu ref={chartRef} title={title} data={data} />
              <UiIcon
                onClick={() => setFullScreen((x) => !x)}
                icon={`ri-${fullScreen ? 'close' : 'eye'}-line`}
              />
              {refresh && (
                <UiIcon onClick={() => refresh()} icon='ri-refresh-line' />
              )}
            </div>
          )}
        </div>
      )}

      <div
        component='CardBody'
        className={classNames(
          'card-body flex',
          !loading && isEmpty(data) ? 'no-data flex' : ''
        )}
      >
        {customFilter}

        <Loading spinning={loading} unVisibleText={true} />
        {!isEmpty(error) && !loading ? (
          <DisplayError error={error} />
        ) : (
          data?.length > 0 &&
          !loading && <div className='chart-container'>{chart}</div>
        )}
        {!loading && isEmpty(data) && isEmpty(error) && (
          <div className='no-data'>{t('common:dataNotFound')}</div>
        )}
      </div>
    </StyledBarLineChart>
  );
};

BarLineChart.propTypes = {
  className: PropTypes.string,
  data: PropTypes.array,
  title: PropTypes.string,
  tooltip: PropTypes.string,
  noHeader: PropTypes.bool,
  loading: PropTypes.bool,
  error: PropTypes.object,
  refresh: PropTypes.func,
  actions: PropTypes.element,
  noActions: PropTypes.bool,
  chartKeys: PropTypes.object,
  legends: PropTypes.object,
  customFilter: PropTypes.element,
  axisLabels: PropTypes.object,
  yAxisDomains: PropTypes.object,
  yAxisTicks: PropTypes.object,
  tooltipOrder: PropTypes.array,
  xAxisKey: PropTypes.string,
  hasLineAnimation: PropTypes.bool,
};

export default BarLineChart;
